// The internal Careforce organization. Careforce-wide (cross-tenant) surfaces key
// on this slug; mirrors CAREFORCE_ORG_SLUG in hoku/auth.py.
export const CAREFORCE_ORG_SLUG = 'careforce'

// Clerk pages org memberships 10 at a time by default. The command palette's
// org switcher drains every page, so a larger page means fewer round trips for
// Careforce staff who belong to every tenant.
export const ORG_SWITCHER_MEMBERSHIPS_PAGE_SIZE = 50

// cmdk's fuzzy score below which a command palette row is hidden. Word-prefix
// matches ("tools" → Tools) score ~0.9 and contiguous partials ("appt" →
// Appointments) ~0.15; letters scattered across a row's keywords ("tools" in
// "/notifications inbox") score under 0.01. cmdk itself shows anything above 0.
export const COMMAND_PALETTE_MIN_MATCH_SCORE = 0.1

// Query cache timings
export const QUERY_STALE_TIME_MS = 30_000 // 30 seconds
export const QUERY_GC_TIME_MS = 5 * 60 * 1000 // 5 minutes
// The org's Nova workflow catalogue is defined on disk in the nova repo, so it
// only changes on deploy. Cached far longer than ordinary server state so
// opening one duty after another reuses a single fetch per session.
export const WORKFLOW_CATALOGUE_STALE_TIME_MS = 60 * 60 * 1000 // 1 hour
export const WORKFLOW_CATALOGUE_GC_TIME_MS = 60 * 60 * 1000 // 1 hour

// Appointments calendar week query: the /data/appointments API caps limit at
// 200 rows per page (AppointmentsQueryParams, hoku/server/models — matches
// relay's own route cap), so a busy week needs multiple pages. The week
// queryFn walks the cursor sequentially (each page needs the prior page's
// cursor), so each page is a full round trip; 200/page halves the number of
// round trips a full range needs versus the previous 100/page. Max pages is
// halved to match, keeping the same 2000-row worst-case cap and `truncated`
// bound as before, in case the server were to keep returning cursors.
export const APPOINTMENTS_PAGE_LIMIT_MAX = 200
export const APPOINTMENTS_AUTO_FETCH_MAX_PAGES = 10
// The client sheet reads one client's whole visit history to count visits and
// derive the care team. hoku's /api/data/visits caps a page at 100 rows
// (VisitsQueryParams); 20 pages bounds a pathological history at 2000 rows and
// reports the rest as truncated.
export const VISITS_PAGE_LIMIT_MAX = 100
export const VISITS_AUTO_FETCH_MAX_PAGES = 20
// Care team chips shown before the rest collapse into a "+N more" count.
export const CLIENT_CARE_TEAM_CHIP_LIMIT = 8
// A care-team provider with no visit in this many months shows as inactive:
// providers carry no active flag of their own, so visit recency stands in.
export const CLIENT_CARE_TEAM_INACTIVE_MONTHS = 12
// Visit types listed in the Clinical tab's visit mix before the rest are left out.
export const CLIENT_VISIT_MIX_LIMIT = 7
// Timeline entries shown per "Load older activity" step.
export const CLIENT_TIMELINE_PAGE_SIZE = 25
// Calls per page in the client sheet's Activity tab (the calls API caps a page
// at 100); older calls load on request.
export const CLIENT_CALLS_PAGE_SIZE = 100
// Lab results and conditions shown in the client sheet's Clinical tab, newest
// first; the full history lives on the Lab Results and Chronic Conditions pages.
export const CLIENT_CLINICAL_PAGE_SIZE = 50
// Calendar day columns render at most this many cards up front; longer days
// get a "Show N more" affordance so a dense clinic day doesn't render
// hundreds of cards nobody has scrolled to yet. High enough that a typical
// day's appointments AND its open-availability blocks (a busy provider
// roster can post far more "Open" blocks than there are bookings) both fit
// before capping kicks in; AppointmentsCalendar still prioritizes
// appointments over open slots as a fallback once a day exceeds this.
export const APPOINTMENTS_DAY_INITIAL_VISIBLE = 50
// The table has no visible date window, but the availability endpoint needs
// both start_time bounds — with Slots shown and no Date filter, slots are
// fetched in windows of this many days starting today. An empty window moves
// on to the next, up to MAX_WINDOWS windows in total (12 weeks).
export const APPOINTMENTS_TABLE_OPEN_SLOTS_DAYS = 14
export const APPOINTMENTS_TABLE_OPEN_SLOTS_MAX_WINDOWS = 6

// Fact autocomplete
export const FACT_SUGGESTION_RESULT_LIMIT = 20
export const FACT_SUGGESTION_PROVIDER_DEBOUNCE_MS = 20
export const FACT_SUGGESTION_PROVIDER_OFFSET_PX = 8
export const FACT_SUGGESTION_COMPOSITION_SETTLE_MS = 30

// Connections page validation polls while Nova opens the configured test patient.
export const CREDENTIAL_VALIDATION_POLL_INTERVAL_MS = 10_000
// Report download checklist polls while a facilities/providers report pull is
// pending or running. Shared with the Print Schedule provider-discovery poll.
export const REPORT_DOWNLOAD_STATUS_POLL_INTERVAL_MS = 10_000

// Cobalt entity sync windows. Mirrors the _MIN_CONFIG_* / _MAX_SYNC_WINDOW_DAYS
// floors in hoku/server/handlers/cobalt_connections.py, which in turn mirror
// relay's CobaltSyncConfigUpdateRequest and the app_cobalt_sync_configs CHECK
// constraints; keep in sync. The two floors are NOT symmetric: a 0-day
// lookback is stored happily, a 0-day lookahead is rejected (relay ge=1 plus a
// DB CHECK), so offering 0 for lookahead only earns a 422.
export const SYNC_LOOKBACK_MIN_DAYS = 0
export const SYNC_LOOKAHEAD_MIN_DAYS = 1
export const SYNC_WINDOW_MAX_DAYS = 365
// Separate bound, and deliberately not the stored-config floor above: relay's
// GET /cobalt/syncs takes referral_lookback_days as 1..365, so a legally
// stored 0 cannot be asked for as an availability window and the page falls
// back to the server default for that count.
export const REFERRAL_AVAILABILITY_MIN_LOOKBACK_DAYS = 1

// Call processing poll settings
export const CALL_PROCESSING_POLL_INTERVAL_MS = 10_000 // 10 seconds
// Test call feedback bar: poll the call until it reports an end, giving up
// after a call no test runs past.
export const TEST_CALL_END_POLL_INTERVAL_MS = 10_000
export const TEST_CALL_END_POLL_MAX_MS = 15 * 60 * 1000
// Active Lines dashboard poll — fast enough to track the allocator's ~1s cadence.
// staleTime matches so data isn't held fresh past a poll.
export const ALLOCATION_BOARD_POLL_INTERVAL_MS = 2_000
// Notification bell — the app's only poll that runs on every page for the whole
// session, so the cadence is the recurring cost. refetchOnWindowFocus covers the
// tab-back case, and the panel refetches on open.
export const NOTIFICATIONS_POLL_INTERVAL_MS = 60_000
// A detached agent run keeps working server-side after a reload or tab
// switch; this is how often the UI checks whether it's done.
export const RUN_STATUS_POLL_INTERVAL_MS = 4_000
export const NOTIFICATIONS_PAGE_SIZE = 25
// Counts above this render as "9+" — the badge sits in a 15px dot on the rail.
export const NOTIFICATIONS_BADGE_MAX = 9

export const CALL_PROCESSING_MAX_AGE_MS = 60 * 60 * 1000 // 1 hour — stops polling stuck/ghost calls

// Chat sidebar sizing
export const CHAT_SIDEBAR_MIN_WIDTH_PX = 400
export const CHAT_SIDEBAR_RESIZE_STEP_PX = 16
export const CHAT_SIDEBAR_RESIZE_STEP_SHIFT_PX = 64
export const CHAT_SIDEBAR_SAFE_MARGIN_PX = 64
// Tailwind's `xl` breakpoint in px. Above it the chat panel stops floating and
// becomes a real grid column, which is what makes the drag clamp tier-aware.
export const XL_BREAKPOINT_PX = 1280
// Keep in sync with `--content-min-width` (40rem) in ui/styles/globals.css —
// the minimum the centre column may shrink to at the inline tier. Read here in
// px because the drag clamp works in px; the CSS clamp is the visual source.
export const CONTENT_MIN_WIDTH_PX = 640

// Sidebar category flyout. The close delay is long enough for the pointer to
// cross the gap from a rail button to the panel, short enough that swapping
// categories doesn't feel sticky. The offset lifts the panel above its anchor
// so the first row sits beside the button that opened it; the margin is the
// minimum gap the panel keeps from either edge of the viewport.
export const SIDEBAR_FLYOUT_CLOSE_DELAY_MS = 180
export const SIDEBAR_FLYOUT_ANCHOR_OFFSET_PX = 44
export const SIDEBAR_FLYOUT_VIEWPORT_MARGIN_PX = 12

// Wait for a brief typing pause before autosaving this write operation.
export const CALL_FEEDBACK_AUTOSAVE_DELAY_MS = 900

// Bound on a single Clerk getToken(). Clerk resolves it behind an internal
// "loaded" promise that never rejects and has no timeout, so an await on it is
// otherwise unbounded and a stuck clerk-js reads as a permanent skeleton.
export const AUTH_TOKEN_TIMEOUT_MS = 10_000

// PER-STAGE bound on recording playback, not an end-to-end budget. Covers the
// /recording-url fetch and play(); the token step has its own bound above.
// Without one, a failure at either stage shows as a spinner that never stops.
export const RECORDING_STAGE_TIMEOUT_MS = 10_000

// Bound on the call-detail fetches. Deliberately looser than the recording
// stage: a long transcript on a cold cache is slow-but-successful, and turning
// that into an error would be worse than the hang this guards against.
export const CALL_DETAIL_TIMEOUT_MS = 30_000

// Bound on the recording download proxy fetch. Looser than
// RECORDING_STAGE_TIMEOUT_MS: this streams the whole audio file through our
// backend (which itself re-resolves a fresh signed URL before piping the
// bytes), not just a redirect lookup, so it legitimately takes longer than
// resolving a playback URL.
export const RECORDING_DOWNLOAD_TIMEOUT_MS = 60_000

// How far playback must get past a failure before automatic recovery re-arms.
// Guards against a recording that fails at the same offset every time looping
// on the resolver, while still letting a genuinely later expiry recover.
export const RECORDING_HEAL_PROGRESS_EPSILON_S = 0.5

// Stop eagerly prefetching a recording URL once one has been invalidated for
// this call — the user has an explicit Retry from that point on.
export const RECORDING_URL_MAX_LOAD_FAILURES = 1

// Memory-pressure tripwires emitted to PostHog as tab counts cross.
export const TAB_COUNT_THRESHOLDS = [8, 12, 20] as const

// Chat eval termination — mirrors DEFAULT_MAX_TURNS / STOP_TOKEN in relay
// `relay/server/services/evals/chat_runner.py`. Keep in sync. The runner auto-
// appends the stop instruction to the persona; the persona ends the run by
// replying with the stop token, otherwise the chat runs to the max-turn cap.
export const CHAT_EVAL_MAX_TURNS = 12
export const CHAT_EVAL_STOP_TOKEN = 'STOP_CHAT_EVAL'

// DiffViewer font size — passed to @git-diff-view/react's diffViewFontSize.
export const DIFF_VIEW_FONT_SIZE_PX = 13
// Longest line a chat ```diff fence may carry and still render as a diff.
// @git-diff-view's inline word diff is quadratic in line length: one 6 KB
// changed pair costs ~12 s on the render thread. Longer bodies stay plain code.
export const CHAT_DIFF_MAX_LINE_CHARS = 2000

// Default counterparty model/provider for chat eval cases created from the UI.
// The relay collapses the counterparty runtime into the case, so the case
// carries these directly.
export const CHAT_EVAL_DEFAULT_MODEL = 'gpt-4o-mini'
export const CHAT_EVAL_DEFAULT_PROVIDER = 'openai'

// David work timeline: longest tool-row detail before it truncates, and how
// many action kinds a tool-group summary names before the rest fold into
// "N other actions".
export const TOOL_DETAIL_MAX_CHARS = 80
export const WORK_SUMMARY_MAX_KINDS = 3
